import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { AccessGroup } from '@/api/auth';
import type { ApiError } from '@/api/http';
import {
  acknowledgePossibleDuplicate,
  type PossibleDuplicateWarning,
  type PersonCard,
} from '@/api/people';
import { queryKeyRoots } from '@/api/queryKeys';
import { Button } from '@/components/ui/Button';
import { mayAcknowledgePossibleDuplicate } from '@/pages/Candidates/candidateAccess';
import { type } from '@/theme/typography';

interface Props {
  person: PersonCard;
  groups: AccessGroup[];
}

/**
 * **The warning an import raised, on the card it was raised for** — `DESIGN.md` §6.5, ADR 0021.
 *
 * ⚠️ **This block is what makes decision 4 honest, and it is the only thing that does.** The ADP
 * import never merges: when a candidate it is creating matches an existing card by one of ADR 0014's
 * three normalised keys, it writes a **second card** and leaves this warning standing open. That is
 * knowingly the state feedback protection #7 exists to prevent — *one person, one card* — and the ADR
 * lists what it costs: the earlier feedback does not surface, ADR 0006's blind gate splits with the
 * card, an erasure performed on one leaves the other populated, and every per-person figure is
 * computed over a fraction of the conversations. **None of that is recoverable by software**; what a
 * human can still do is make the connection, which they can only do if they are told. *"For imported
 * people, this product's seventh protection stops being a property of the data and becomes a property
 * of somebody's attention."*
 *
 * ⚠️ **It carries ids and key names, never the other person's data, and that is the row's shape rather
 * than this component's reticence.** `PossibleDuplicateWarningOut` holds the other card's **id** and
 * which of the three keys matched; §6.4's `DuplicateMatches` renders a name, contacts and runs because
 * its payload is the body of a **409** — a bounded disclosure inside one request. This row *outlives*
 * the request and is read months later, so it holds no personal data at all, which is why ADR 0009 has
 * nothing to clear on it. The reader follows the link and reads that card through the ordinary
 * tenant-scoped route, which was already theirs to read. **Reusing `DuplicateMatches` here was the
 * obvious move and is refused for exactly that reason**: it is built to print a name this payload
 * deliberately does not carry, and fetching the other card to fill one in would put the data back.
 *
 * ⚠️ **The acknowledgement is a route, not a dismissal held in this component** (ADR 0021): a warning
 * that un-warns itself on a reload is not a warning. The call invalidates the card and the state is
 * read back off the refetch — the server stops sending a warning somebody has closed
 * (`services.open_possible_duplicates`), so what survives a reload is the server's answer and never a
 * flag here.
 *
 * **Every reader sees the warning; only a writer sees the action.** An `Interviewer` reading a card
 * they were assigned to is precisely the person ADR 0021's list of losses is about — they are the one
 * who will not see the earlier feedback — so the sentence is theirs too. The act is a judgement about
 * the tenant's candidate records, and the route answers them **403** (§5: not rendered, never
 * disabled).
 */
export function PossibleDuplicates({ person, groups }: Props) {
  const { t } = useTranslation(['candidates', 'components']);

  if (person.possible_duplicates.length === 0) return null;

  return (
    <section
      /*
       * `role="status"` rather than `alert`: it is a standing fact about the card found on load, not
       * an answer to something the reader just did, and §4 keeps `alert` for the latter. It is not a
       * `Toast` either — a toast is for something that succeeded and needs no answer, and this needs
       * one.
       */
      role="status"
      aria-label={t('candidates:card.possibleDuplicates.label')}
      className="mt-4 flex flex-col gap-4 rounded border border-rule-control bg-paper-sunken p-4"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-ink-900">
          {t('candidates:card.possibleDuplicates.heading', {
            count: person.possible_duplicates.length,
          })}
        </p>
        {/*
         * What the import did, said plainly — the half ADR 0021 insists is stated rather than implied.
         * A reader who is not told that two cards each hold part of the history has been shown a
         * warning they cannot act on.
         */}
        <p className={type.metadata}>{t('candidates:card.possibleDuplicates.whatHappened')}</p>
      </div>

      <ul className="flex flex-col gap-4">
        {person.possible_duplicates.map((warning) => (
          <Warning key={warning.id} warning={warning} groups={groups} />
        ))}
      </ul>
    </section>
  );
}

/**
 * One warning: **which keys matched**, the way to the other card, and the act that closes it.
 *
 * The key words are §6.4's own (`duplicate.keys.*`, `duplicate.matchedOn`), deliberately: a recruiter
 * meeting *"Matched on name, email."* in the create dialog and here is meeting one sentence, and only
 * they can answer whether two Ada Lovelaces are one person.
 */
function Warning({
  warning,
  groups,
}: {
  warning: PossibleDuplicateWarning;
  groups: AccessGroup[];
}) {
  const { t } = useTranslation(['candidates', 'components']);
  const queryClient = useQueryClient();

  const acknowledge = useMutation<unknown, ApiError, void>({
    mutationFn: () => acknowledgePossibleDuplicate(warning.id),
    /*
     * ⚠️ **Invalidate and refetch; the answer is deliberately not written into the cache.** The route
     * answers the warning row itself, which says nothing about the rest of the card, and the thing
     * that must be true afterwards is that the **server** no longer sends it. Reading the new state
     * back off the refetch is what makes the acknowledgement survive a reload rather than survive
     * only this tab.
     */
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.person });
    },
  });

  /** Which of ADR 0014's three keys matched, in §6.4's words. */
  const keys = [
    warning.matched_full_name && t('candidates:duplicate.keys.full_name'),
    warning.matched_email && t('candidates:duplicate.keys.email'),
    warning.matched_phone && t('candidates:duplicate.keys.phone'),
  ].filter((key): key is string => typeof key === 'string');

  const failure = (() => {
    if (!acknowledge.error) return null;
    // ⚠️ The **write** sentence (BUG-017): nothing here queues, and an acknowledgement somebody
    // believes was queued is a warning they will not read again.
    if (acknowledge.error.isOffline) return t('components:state.offlineWrite');
    if (acknowledge.error.isThrottled) return t('components:state.throttled');
    return t('candidates:card.possibleDuplicates.failed');
  })();

  return (
    <li className="flex flex-col items-start gap-2">
      <p className={type.metadata}>
        {keys.length > 0
          ? t('candidates:duplicate.matchedOn', { keys: keys.join(', ') })
          : t('candidates:duplicate.matchedOnUnnamed')}
      </p>

      {/*
       * The other card, by id — the shortest way to answer the only question the reader can answer,
       * and the only way this payload offers to learn anything about it. It is a real `<a>`, so the
       * two cards can be put side by side in two tabs, which is what comparing them actually takes.
       */}
      <Link
        to={`/candidates/${warning.other_person_id}`}
        className="rounded text-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        {t('candidates:card.possibleDuplicates.openOther')}
      </Link>

      {mayAcknowledgePossibleDuplicate(groups) && (
        <Button
          variant="secondary"
          size="sm"
          isLoading={acknowledge.isPending}
          onClick={() => acknowledge.mutate()}
        >
          {t('candidates:card.possibleDuplicates.acknowledge')}
        </Button>
      )}

      {failure && (
        <p role="alert" className="text-sm text-negative">
          {failure}
        </p>
      )}
    </li>
  );
}
